import { Link } from "react-router-dom";
import { Mail, Lock, User, Briefcase, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/lib/supabase";

export function Signup() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    password: "",
    agreeToTerms: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signedUp, setSignedUp] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const { error } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: {
        data: { display_name: formData.fullName, company: formData.company },
      },
    });

    if (error) {
      setError(error.message);
      setIsLoading(false);
    } else {
      setSignedUp(true);
    }
  };

  if (signedUp) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-[#FD802E] font-bold text-3xl mb-2">Searchline Engine</h1>
          </div>

          <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-8 text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-400" size={32} />
            </div>
            <h2 className="text-white text-2xl font-bold mb-3">Check your email</h2>
            <p className="text-gray-400 mb-6">
              We've sent a confirmation link to{" "}
              <span className="text-white font-medium">{formData.email}</span>.
              Click it to activate your account.
            </p>
            <Link
              to="/login"
              className="block text-center text-[#FD802E] hover:text-[#ff8f45] font-medium transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02182B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-[#FD802E] font-bold text-3xl mb-2">Searchline Engine</h1>
          <p className="text-gray-400">Start your 14-day free trial</p>
        </div>

        {/* Signup Card */}
        <div className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-8">
          <h2 className="text-white text-2xl font-bold mb-6">Create your account</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-gray-300 text-sm font-medium mb-2">
                Full name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Smith"
                  required
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">
                Work email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@company.com"
                  required
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
                />
              </div>
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-gray-300 text-sm font-medium mb-2">
                Company name
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Acme Corp"
                  required
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg pl-11 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-gray-300 text-sm font-medium mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  className="w-full bg-[#02182B] border border-[#2a4a5c] rounded-lg pl-11 pr-11 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-1">
                Must be at least 8 characters with a mix of letters and numbers
              </p>
            </div>

            {/* Terms Agreement */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.agreeToTerms}
                  onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                  required
                  className="w-4 h-4 mt-0.5 rounded border-gray-600 bg-[#02182B] text-[#FD802E] focus:ring-[#FD802E] focus:ring-offset-0"
                />
                <span className="text-gray-400 text-sm">
                  I agree to the{" "}
                  <a href="#" className="text-[#FD802E] hover:text-[#ff8f45]">Terms of Service</a>
                  {" "}and{" "}
                  <a href="#" className="text-[#FD802E] hover:text-[#ff8f45]">Privacy Policy</a>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#FD802E] text-[#233D4C] py-3 rounded-lg font-semibold hover:bg-[#ff8f45] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating account…" : "Create account"}
            </button>
          </form>

          {/* Login Link */}
          <p className="text-center text-gray-400 text-sm mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FD802E] hover:text-[#ff8f45] font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>

        {/* Footer Note */}
        <div className="bg-[#233D4C]/50 rounded-lg border border-[#2a4a5c] p-4 mt-4">
          <p className="text-gray-400 text-sm text-center">
            🎁 <span className="text-white font-medium">14-day free trial</span> · No credit card required
          </p>
        </div>
      </div>
    </div>
  );
}
